import { z } from "zod";

import {
  VibeDeploymentColorReadSchema,
  VibeTriggerSourceReadSchema
} from "../../../api/domains/vibe/schemas/enum-read.schemas";
import { DbEnum } from "../../../generated/prisma-enums";
import { VibeFailureReasonCodeReadSchema } from "../../../shared/domain/vibe/vibe-failure-reason-code";
import {
  VibeAuditBuildJobStatusReadSchema,
  VibeAuditBuildSucceededNextStatusReadSchema,
  VibeAuditDeploymentStatusReadSchema
} from "./read-vocabularies.schema";

/**
 * Emitted when a deployment row is triggered (the composite TX
 * persisting the deployment + build job + optional approval request
 * commits). The trigger flow attributes the deploy to the user who
 * pushed; system-triggered deploys (future webhook handler) write
 * with `actorUserId = null` and surface the system context via the
 * payload metadata.
 */
export const VibeAuditDeploymentTriggeredPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_TRIGGERED"),
  vibeDeploymentId: z.string().uuid(),
  triggerSha: z.string().min(1).max(64),
  /// True iff the app had `requireApprovals = true` at trigger time
  /// (so a VibeApprovalRequest sibling row was created in the same
  /// TX). Lets the audit feed distinguish ungated → BUILDING → live
  /// from gated → AWAITING_APPROVAL → live without re-reading the
  /// deployment row.
  approvalGated: z.boolean(),
  /// What kicked this deploy off: a push to the deploy branch, `nexus vibe
  /// deploy`, or the console. Mirrors `VibeDeployment.triggerSource`.
  ///
  /// Duplicated onto the audit row deliberately. The audit feed is what an
  /// operator reaches for to answer "why did this app deploy twice for one
  /// commit", and it is readable by the org that owns the tenant, where the
  /// deployment column is not. Without it that question needs a second query
  /// the asker often cannot make.
  ///
  /// OPTIONAL, and it has to be: `VibeAuditEvent.fromPrisma` safeParses
  /// `payloadJson` and DROPS the row from the feed when it fails, so making
  /// this required would silently erase every DEPLOYMENT_TRIGGERED row
  /// written before this field existed. Absent means "predates the field",
  /// never "unknown source".
  triggerSource: z.enum(DbEnum.VIBE_TRIGGER_SOURCE_VALUES).optional()
});
export type VibeAuditDeploymentTriggeredPayload = z.infer<
  typeof VibeAuditDeploymentTriggeredPayloadSchema
>;

/**
 * The same row as an older console or CLI reads it off the audit feed: a trigger
 * source this contract does not list parses as the server's own word instead of
 * failing the whole feed page. The emitting branch above stays strict, so the
 * backend still cannot write a source it does not list; only the READ widens —
 * exactly as the deployment's own `triggerSource` is read. `enumReadSchema`
 * carries the reasoning.
 */
export const VibeAuditDeploymentTriggeredReadPayloadSchema =
  VibeAuditDeploymentTriggeredPayloadSchema.extend({
    triggerSource: VibeTriggerSourceReadSchema.optional()
  });

/**
 * Emitted by the build-runner post-success composite TX when a
 * VibeDeployment flips BUILDING -> (AWAITING_APPROVAL | DEPLOYING)
 * with the build's `imageRef` stamped. Paired with BUILD_JOB_SUCCEEDED
 * in the same `$transaction`. The pair lets the audit feed answer
 * "what shipped on deployment X?" with one query (imageRef + the
 * approval-gate branching).
 *
 * `nextStatus` is constrained to the two valid target statuses for
 * this transition: AWAITING_APPROVAL when a trigger-time approval
 * request existed, DEPLOYING otherwise. Encoding the resolved status
 * in the payload (rather than re-deriving from the approval-request
 * row at read time) keeps the audit feed self-contained — an admin
 * reading the row gets the full handoff narrative without N
 * follow-up queries. `requiresApproval` is the input that drove the
 * branching decision, retained for the same diagnostic reason.
 */
export const VibeAuditDeploymentBuildSucceededPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_BUILD_SUCCEEDED"),
  vibeDeploymentId: z.string().uuid(),
  vibeBuildJobId: z.string().uuid(),
  /// ECR image ref the build runner produced. Non-empty per the
  /// entity invariant (markBuildSucceeded requires imageRef set).
  imageRef: z.string().min(1),
  /// True iff the trigger-time approval-request row existed (the
  /// trigger-time marker, NOT the app's current `requireApprovals`
  /// config — mid-flight config changes don't retroactively decide
  /// this). True implies `nextStatus === AWAITING_APPROVAL`; false
  /// implies `nextStatus === DEPLOYING`. Both halves of the
  /// invariant are retained so the audit feed is self-describing.
  requiresApproval: z.boolean(),
  /// The resolved target status. Narrowed to the only two statuses
  /// the build-success transition can advance to — any other value
  /// is a programmer error at the entity layer.
  nextStatus: z.enum(["AWAITING_APPROVAL", "DEPLOYING"])
});
export type VibeAuditDeploymentBuildSucceededPayload = z.infer<
  typeof VibeAuditDeploymentBuildSucceededPayloadSchema
>;

/**
 * Emitted when a deployment flips to FAILED from a non-terminal
 * status. The autonomous deployer writes this from its dispatch-
 * failure compensation path (DEPLOYING → FAILED) INSIDE the same
 * `$transaction` as the deployment UPDATE, so the audit row + the
 * FAILED flip land atomically, per
 * `feedback_swallowed_fire_and_forget_silent_failure`. The admin
 * manual escape-hatch (`MarkVibeDeploymentFailedUseCase`) does NOT
 * emit this — operator-driven transitions are captured by the admin
 * AuditAction trail, mirroring the SUCCEEDED / BUILD_JOB_FAILED split.
 *
 * `markFailed` is multi-source (BUILDING | AWAITING_APPROVAL |
 * DEPLOYING), so the payload records `priorStatus` — diagnostic for
 * "did the build fail, was it rejected, or did the deploy dispatch
 * fail?". v1's only emitter (the deployer compensation) always passes
 * DEPLOYING; the build-runner deployment-side FAILED path and the
 * approval-rejection path will populate the other priors when they
 * wire this event type in.
 */
export const VibeAuditDeploymentFailedPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_FAILED"),
  vibeDeploymentId: z.string().uuid(),
  /// The non-terminal status the deployment failed FROM. Typed as the
  /// full generated `VibeDeploymentStatus` enum (drift-proof, no
  /// hardcoded literals) rather than a narrow failable subset: the
  /// audit records whichever status the writer observed, and the
  /// entity's `markFailed` source-guard is the enforcement layer that
  /// keeps the value within BUILDING | AWAITING_APPROVAL | DEPLOYING.
  priorStatus: z.enum(DbEnum.VIBE_DEPLOYMENT_STATUS_VALUES),
  /// Customer-visible surface text. No max length — capping here would
  /// make `VibeAuditEvent.create` throw on a long failure message and
  /// turn a deploy failure into a 500. Non-empty per the entity
  /// contract (`markFailed` requires `errorReason`).
  errorReason: z.string().min(1),
  /// Why the deployment failed, as the structured code the console classifies on —
  /// never `errorReason`, which carries the tenant's own build output. Read
  /// leniently: a row written before the field existed, or carrying a code this
  /// build does not know, reads as `null` (the generic failure) instead of
  /// failing the row's parse and dropping it from the feed.
  reasonCode: VibeFailureReasonCodeReadSchema,
  /// The commit sha so the user can re-trigger after a fix without
  /// log-scraping. Same bounds as the DEPLOYMENT_TRIGGERED payload's
  /// `triggerSha`.
  triggerSha: z.string().min(1).max(64)
});
export type VibeAuditDeploymentFailedPayload = z.infer<
  typeof VibeAuditDeploymentFailedPayloadSchema
>;

/**
 * Emitted when a deployment flips DEPLOYING -> HEALTHY. The deploy
 * executor's health-check callback webhook writes this INSIDE the same
 * `$transaction` as the deployment UPDATE, so the audit row + the HEALTHY
 * flip land atomically, per
 * `feedback_swallowed_fire_and_forget_silent_failure`. The admin manual
 * escape-hatch (`MarkVibeDeploymentHealthyUseCase`) does NOT emit this —
 * operator-driven transitions are captured by the admin AuditAction
 * trail, mirroring the FAILED / SUCCEEDED splits.
 *
 * `imageRef` records what shipped (the ECR ref the executor placed),
 * `color` the blue/green slot now serving traffic, `triggerSha` the
 * commit — together the audit feed answers "what went live on deployment
 * X?" from the row alone.
 */
export const VibeAuditDeploymentHealthyPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_HEALTHY"),
  vibeDeploymentId: z.string().uuid(),
  /// ECR image ref that went live. Non-empty per the deployer contract
  /// (a deployment only reaches DEPLOYING with `imageRef` stamped by the
  /// build runner).
  imageRef: z.string().min(1),
  /// Blue/green slot now serving traffic. Derived from the generated enum
  /// (no hardcoded literals).
  color: z.enum(DbEnum.VIBE_DEPLOYMENT_COLOR_VALUES),
  /// Commit sha that went live. Same bounds as the DEPLOYMENT_TRIGGERED
  /// payload's `triggerSha`.
  triggerSha: z.string().min(1).max(64)
});
export type VibeAuditDeploymentHealthyPayload = z.infer<
  typeof VibeAuditDeploymentHealthyPayloadSchema
>;

/**
 * Emitted when the blue-green cutover retires a previously-healthy
 * deployment (HEALTHY -> SUPERSEDED) because a newer deployment of the
 * opposite color went HEALTHY and took over serving traffic. The cutover
 * stops the retired deployment's executor job and writes this audit
 * INSIDE the same `$transaction` as the SUPERSEDED flip, so the audit row
 * + the status flip land atomically.
 *
 * Distinct from DEPLOYMENT_ROLLED_BACK_HEALTH_CHECK: that records a deploy
 * whose OWN health check failed (traffic stayed on the prior color);
 * SUPERSEDED records a clean, successful handoff to a successor. Keeping
 * the two apart lets an operator tell "this deploy failed" from "this
 * deploy was replaced" without inspecting siblings.
 *
 * `color` is the retired blue/green slot, `triggerSha` the retired commit,
 * and `supersededByDeploymentId` the successor whose HEALTHY transition
 * caused the cutover — together the audit feed answers "why was deployment
 * X retired?" from the row alone. `actorUserId` is null (system action).
 */
export const VibeAuditDeploymentSupersededPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_SUPERSEDED"),
  vibeDeploymentId: z.string().uuid(),
  /// Blue/green slot the retired deployment occupied. Derived from the
  /// generated enum (no hardcoded literals).
  color: z.enum(DbEnum.VIBE_DEPLOYMENT_COLOR_VALUES),
  /// Commit sha the retired deployment shipped. Same bounds as the
  /// DEPLOYMENT_TRIGGERED payload's `triggerSha`.
  triggerSha: z.string().min(1).max(64),
  /// The successor deployment whose HEALTHY transition triggered the
  /// cutover — the cause of this retirement. The opposite color of
  /// `color` by the blue/green alternation invariant.
  supersededByDeploymentId: z.string().uuid()
});
export type VibeAuditDeploymentSupersededPayload = z.infer<
  typeof VibeAuditDeploymentSupersededPayloadSchema
>;

/**
 * Emitted when the deployer terminates a deployment that a newer one displaced
 * as the app's rollout target before it could go live. Written INSIDE the
 * same status-guarded `$transaction` as the DEPLOYING -> DISPLACED flip, so a
 * terminal deployment can never exist without the audit row explaining it.
 *
 * Distinct from DEPLOYMENT_SUPERSEDED: that retires a deployment which SERVED
 * traffic and was cleanly replaced by a successor that went healthy. A
 * displaced deployment never served — an app has exactly one rollout target,
 * and the agent converges on the newer row.
 *
 * Distinct from DEPLOYMENT_FAILED because nothing failed. A displaced
 * deployment has no executor left to report on it and waits for nothing — its
 * outcome is settled the instant a newer deployment exists.
 *
 * `color` is the slot the displaced deployment held, `triggerSha` the commit it
 * would have shipped, and `displacedByDeploymentId` the newer deployment that
 * took the rollout — together the feed answers "why did deployment X never
 * deploy?" from the row alone. `actorUserId` is null (the deployer cron is a
 * system actor).
 */
export const VibeAuditDeploymentDisplacedPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_DISPLACED"),
  vibeDeploymentId: z.string().uuid(),
  /// Status the row held when it was displaced. DEPLOYING on today's only
  /// path, but recorded rather than assumed, mirroring DEPLOYMENT_FAILED.
  priorStatus: z.enum(DbEnum.VIBE_DEPLOYMENT_STATUS_VALUES),
  /// Blue/green slot the displaced deployment held. Derived from the
  /// generated enum (no hardcoded literals).
  color: z.enum(DbEnum.VIBE_DEPLOYMENT_COLOR_VALUES),
  /// Commit sha the displaced deployment would have shipped. Same bounds as
  /// the DEPLOYMENT_TRIGGERED payload's `triggerSha`.
  triggerSha: z.string().min(1).max(64),
  /// The newer deployment that became the rollout target and starved this
  /// one. Never equal to `vibeDeploymentId`.
  displacedByDeploymentId: z.string().uuid()
});
export type VibeAuditDeploymentDisplacedPayload = z.infer<
  typeof VibeAuditDeploymentDisplacedPayloadSchema
>;

/**
 * Emitted when a person stops a deployment's build while it is queued or
 * running. Written in the same `$transaction` as the build job's move to
 * CANCELLED and the deployment's BUILDING -> CANCELLED, so a cancelled
 * deployment never exists without the row naming who asked — `actorUserId` is
 * that person, and null for an org API key, which carries no user.
 *
 * Distinct from DEPLOYMENT_DISPLACED, which the platform writes when a newer
 * deployment takes this one's place, and from DEPLOYMENT_FAILED: nothing failed.
 */
export const VibeAuditDeploymentCancelledPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_CANCELLED"),
  vibeDeploymentId: z.string().uuid(),
  vibeBuildJobId: z.string().uuid(),
  /// The build status the cancel stopped it from — queued or in flight. Recorded
  /// rather than inferred: whether it had started decides whether it was metered.
  priorBuildStatus: z.enum(DbEnum.VIBE_BUILD_JOB_STATUS_VALUES),
  color: z.enum(DbEnum.VIBE_DEPLOYMENT_COLOR_VALUES),
  /// Same bounds as the DEPLOYMENT_TRIGGERED payload's `triggerSha`.
  triggerSha: z.string().min(1).max(64)
});
export type VibeAuditDeploymentCancelledPayload = z.infer<
  typeof VibeAuditDeploymentCancelledPayloadSchema
>;

/**
 * Emitted when the app's public URL was observed answering FROM this
 * deployment — the terminal "it is actually live".
 *
 * `DEPLOYMENT_HEALTHY` is the ALLOCATION's verdict: a Nomad check hitting the
 * container's own port, which passes before the tenant's Traefik has polled the
 * catalog and before an edge cache ahead of it has released the previous build.
 * Measured lag to the served swap is up to whole minutes, so a client that
 * verifies a deploy on HEALTHY reads the OLD content and reports a
 * wrong-code-shipped incident (NEX-2728, NEX-2805). This row is the event that
 * answers the question HEALTHY was being asked and could not answer.
 *
 * Written at most once per deployment, by the served-probe sweep, when a fetch
 * of the public URL over the PUBLIC internet — the same path a client curls —
 * comes back naming this deployment in the edge's `X-Vibe-Deployment` header.
 * A deployment that never serves gets NO row: its failure is already told by
 * ROLLED_BACK / FAILED / DISPLACED, and a per-tick "still not served" row would
 * bury this event under its own repetitions.
 *
 * The payload stands alone. `triggerSha` and `imageRef` are copied in rather
 * than left to a join, so the row still answers "what went live, and when did it
 * actually start being served?" after the deployment has been superseded — which
 * on a busy app is minutes later.
 */
export const VibeAuditDeploymentServedPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_SERVED"),
  vibeDeploymentId: z.string().uuid(),
  /// Commit whose build is now being served. Same bounds as the
  /// DEPLOYMENT_TRIGGERED payload's `triggerSha`.
  triggerSha: z.string().min(1).max(64),
  /// ECR image ref being served. Non-empty per the deployer contract.
  imageRef: z.string().min(1),
  /// Blue/green slot the response came from. Derived from the generated enum
  /// (no hardcoded literals).
  color: z.enum(DbEnum.VIBE_DEPLOYMENT_COLOR_VALUES),
  /// Milliseconds from the deployment being marked HEALTHY to this observation —
  /// the lag these tickets are about, measured instead of asserted.
  ///
  /// Always present. The sweep only considers a deployment whose
  /// `DEPLOYMENT_HEALTHY` row it can see, because that row is also what makes
  /// its dedup sound — so there is no path that produces a served row with no
  /// healthy instant to measure from, and a nullable field here would be a
  /// branch every reader writes and nothing ever takes.
  ///
  /// An UPPER bound on the real lag, never the lag itself: the probe samples on
  /// a tick, so it can only ever notice the swap some time after it happened.
  /// Useful for "is this getting worse?", wrong for an SLO.
  healthyToServedMs: z.number().int().nonnegative()
});
export type VibeAuditDeploymentServedPayload = z.infer<
  typeof VibeAuditDeploymentServedPayloadSchema
>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
const VibeAuditDeploymentColorRead = { color: VibeDeploymentColorReadSchema };

export const VibeAuditDeploymentBuildSucceededReadPayloadSchema =
  VibeAuditDeploymentBuildSucceededPayloadSchema.extend({
    nextStatus: VibeAuditBuildSucceededNextStatusReadSchema
  });
export const VibeAuditDeploymentFailedReadPayloadSchema =
  VibeAuditDeploymentFailedPayloadSchema.extend({
    priorStatus: VibeAuditDeploymentStatusReadSchema
  });
export const VibeAuditDeploymentHealthyReadPayloadSchema =
  VibeAuditDeploymentHealthyPayloadSchema.extend(VibeAuditDeploymentColorRead);
export const VibeAuditDeploymentSupersededReadPayloadSchema =
  VibeAuditDeploymentSupersededPayloadSchema.extend(VibeAuditDeploymentColorRead);
export const VibeAuditDeploymentDisplacedReadPayloadSchema =
  VibeAuditDeploymentDisplacedPayloadSchema.extend({
    ...VibeAuditDeploymentColorRead,
    priorStatus: VibeAuditDeploymentStatusReadSchema
  });
export const VibeAuditDeploymentCancelledReadPayloadSchema =
  VibeAuditDeploymentCancelledPayloadSchema.extend({
    ...VibeAuditDeploymentColorRead,
    priorBuildStatus: VibeAuditBuildJobStatusReadSchema
  });
export const VibeAuditDeploymentServedReadPayloadSchema =
  VibeAuditDeploymentServedPayloadSchema.extend(VibeAuditDeploymentColorRead);
